import {
  useContext,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import { Box, TextField, Checkbox } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PanoramaFishEyeIcon from "@mui/icons-material/PanoramaFishEye";
import { useLocation } from "react-router-dom";
import PrefectureSelect from "../../../../components/PrefectureSelect";
import CustomerSelect from "../../../../components/CustomerSelect";
import BranchSelect from "../../../../components/BranchSelect";
import SearchResult from "./SearchResult";
import { TokenContext } from "../../../../TokenContext";
import { IsSSAgent } from "../../../../role";
import type {
  SearchAreaHandle,
  SearchResultHandle,
  SituationCheckSearchContents,
} from "./types";

const SearchArea = forwardRef<SearchAreaHandle>((_props, ref) => {
  const searchResultRef = useRef<SearchResultHandle | null>(null);
  //クエリパラメータ取得
  const searchParams = new URLSearchParams(useLocation().search);
  const disasterId = searchParams.get("disaster-id");

  // 検索条件
  const [searchContents, setSearchContents] = useState<SituationCheckSearchContents>({
    display_id: disasterId,
    display_code: null,
    name: null,
    pref: null,
    m_branch_id: null, // 例: 都道府県コードを指定 (複数指定可能)
    m_customer_id: null, // 例: 得意先IDを指定 (複数指定可能)
    latest_alive: null,
    latest_sales: null,
    latest_authori: null,
    is_input: null,
    is_damage: null,
  });

  const { user } = useContext(TokenContext)!;
  const isSSAgent = user ? IsSSAgent(user.m_organization.m_role_id) : false;
  useEffect(() => {
    if (!user || !isSSAgent) return;
    const ownSites = user.site_users ?? [];
    if (ownSites.length > 0) {
      const displayCode = ownSites[0].site?.display_code;
      if (typeof displayCode === "string") {
        setSearchContents((prev) => ({
          ...prev,
          display_code: displayCode,
          display_code_disabled: true,
        }));
      }
    }
  }, [isSSAgent, user]);

  // URL の disaster-id が変わったとき（同一画面内のクエリ更新含む）検索の表示 ID を同期する
  useEffect(() => {
    setSearchContents((prev) => ({
      ...prev,
      display_id: disasterId,
    }));
  }, [disasterId]);

  // 親コンポーネントから呼び出せる関数を公開
  useImperativeHandle(ref, () => ({
    batchRegister: () => {
      if (searchResultRef.current) {
        searchResultRef.current.batchRegister();
      }
    },
  }), []);

  //ロケーションコード設定
  const handleChangeLocathion = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSearchContents((prev) => ({
      ...prev,
      display_code: e.target.value,
    }));
  };

  const handleChangePref = (e: string | string[] | null) => {
    const pref = Array.isArray(e) ? e[0] ?? null : e;
    console.log("handleChangePref", e);
    setSearchContents((prev) => ({
      ...prev,
      pref,
    }));
  };

  const handleChangeBranch = (e: number | number[] | null) => {
    const branchId = Array.isArray(e) ? e[0] ?? null : e;
    setSearchContents((prev) => ({
      ...prev,
      m_branch_id: branchId,
    }));
  };

  const handleChangeCustomer = (e: number | number[] | null) => {
    const customerId = Array.isArray(e) ? e[0] ?? null : e;
    setSearchContents((prev) => ({
      ...prev,
      m_customer_id: customerId,
    }));
  };

  // 被害報告入力有無
  const handleChangeIsInput = (e: boolean) => {
    if (searchContents.is_input === e) {
      // チェックされているボックスが再度クリックされた場合、チェックを外す
      setSearchContents((prev) => ({
        ...prev,
        is_input: null,
      }));
    } else {
      // チェックされたボックスに対応する値を設定
      setSearchContents((prev) => ({
        ...prev,
        is_input: e,
      }));
    }
  };

  // 被害有無
  const handleChangeIsDamage = (e: boolean) => {
    if (searchContents.is_damage === e) {
      // チェックされているボックスが再度クリックされた場合、チェックを外す
      setSearchContents((prev) => ({
        ...prev,
        is_damage: null,
      }));
    } else {
      // チェックされたボックスに対応する値を設定
      setSearchContents((prev) => ({
        ...prev,
        is_damage: e,
      }));
    }
  };

  //直近売上設定
  const handleChangeSales = (e: boolean) => {
    if (searchContents.latest_sales === e) {
      // チェックされているボックスが再度クリックされた場合、チェックを外す
      setSearchContents((prev) => ({
        ...prev,
        latest_sales: null,
      }));
    } else {
      // チェックされたボックスに対応する値を設定
      setSearchContents((prev) => ({
        ...prev,
        latest_sales: e,
      }));
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "fit-content",
        justifyContent: "center",
        borderRadius: 2,
        borderColor: "primary.main",
        boxShadow: 3,
        color: "outline.dark",
        backgroundColor: "#fff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "fit-content",
          justifyContent: "center",
          gap: "16px",
          paddingTop: "32px",
          paddingRight: "40px",
          paddingLeft: "40px",
          "@media screen and (max-width:900px)": {
            padding: "16px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: "min-content",
            paddingLeft: "40px",
            fontSize: 16,
            fontWeight: "bold",
            color: "#000",
            whiteSpace: "nowrap",
            "@media screen and (max-width:900px)": {
              padding: "0",
            },
          }}
        >
          検索条件
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "16px",
            "@media screen and (max-width:600px)": {
              flexDirection: "column",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              width: "100%",
              maxWidth: "480px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "min-content",
                fontSize: 16,
                fontWeight: "bold",
                color: "#000",
                whiteSpace: "nowrap",
                "@media screen and (max-width:900px)": {
                  flexDirection: "column",
                  width: "100%",
                  alignItems: "flex-start",
                },
              }}
            >
              <Box sx={{ minWidth: "160px" }}>Sコード</Box>
              <Box sx={{ display: "flex", width: "100%" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px",
                    height: "40px",
                    border: "1px solid #CCC",
                    borderRight: "none",
                    borderTopLeftRadius: "4px",
                    borderBottomLeftRadius: "4px",
                  }}
                >
                  S
                </Box>
                <TextField
                  id="outlined-basic"
                  name="sCodeInput"
                  variant="outlined"
                  value={searchContents.display_code || ""}
                  disabled={searchContents.display_code_disabled}
                  onChange={(e) => handleChangeLocathion(e)}
                  sx={{
                    height: "40px",
                    width: "100%",
                    ".MuiInputBase-root": {
                      height: "100%",
                    },
                    "#outlined-basic": {
                      boxSizing: "border-box",
                      height: "100%",
                    },
                    ".MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderRadius: "0 4px 4px 0",
                      },
                    },
                  }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "min-content",
                fontSize: 16,
                fontWeight: "bold",
                color: "#000",
                whiteSpace: "nowrap",
                "@media screen and (max-width:900px)": {
                  flexDirection: "column",
                  width: "100%",
                  alignItems: "flex-start",
                },
              }}
            >
              <Box sx={{ minWidth: "160px" }}>都道府県</Box>
              <PrefectureSelect
                branchId={searchContents.m_branch_id ?? null}
                customerId={searchContents.m_customer_id ?? null}
                changedValue={(value) => handleChangePref(value)}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "min-content",
                fontSize: 16,
                fontWeight: "bold",
                color: "#000",
                whiteSpace: "nowrap",
                "@media screen and (max-width:900px)": {
                  flexDirection: "column",
                  width: "100%",
                  alignItems: "flex-start",
                },
              }}
            >
              <Box sx={{ minWidth: "160px" }}>特約店</Box>
              <CustomerSelect
                prefCode={searchContents.pref ?? null}
                branchId={searchContents.m_branch_id ?? null}
                changedValue={(value) => handleChangeCustomer(value)}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "min-content",
                fontSize: 16,
                fontWeight: "bold",
                color: "#000",
                whiteSpace: "nowrap",
                "@media screen and (max-width:900px)": {
                  flexDirection: "column",
                  width: "100%",
                  alignItems: "flex-start",
                },
              }}
            >
              <Box sx={{ minWidth: "160px" }}>支店</Box>
              <BranchSelect
                prefCode={searchContents.pref ?? null}
                customerId={searchContents.m_customer_id ?? null}
                changedValue={(value) => handleChangeBranch(value)}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              width: "100%",
              maxWidth: "480px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "min-content",
                fontSize: 16,
                fontWeight: "bold",
                color: "#000",
                whiteSpace: "nowrap",
                "@media screen and (max-width:900px)": {
                  flexDirection: "column",
                  width: "100%",
                  alignItems: "flex-start",
                },
              }}
            >
              <Box sx={{ minWidth: "160px" }}>被害報告入力有無</Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  width: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    name="damageReportInputTrue"
                    size="small"
                    checked={searchContents.is_input === true}
                    onChange={() => handleChangeIsInput(true)}
                  />
                  <PanoramaFishEyeIcon />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    name="damageReportInputFalse"
                    size="small"
                    checked={searchContents.is_input === false}
                    onChange={() => handleChangeIsInput(false)}
                  />
                  <CloseIcon />
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "min-content",
                fontSize: 16,
                fontWeight: "bold",
                color: "#000",
                whiteSpace: "nowrap",
                "@media screen and (max-width:900px)": {
                  flexDirection: "column",
                  width: "100%",
                  alignItems: "flex-start",
                },
              }}
            >
              <Box sx={{ minWidth: "160px" }}>被害有無</Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  width: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    name="damageTrue"
                    size="small"
                    checked={searchContents.is_damage === true}
                    onChange={() => handleChangeIsDamage(true)}
                  />
                  <PanoramaFishEyeIcon />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    name="damageFalse"
                    size="small"
                    checked={searchContents.is_damage === false}
                    onChange={() => handleChangeIsDamage(false)}
                  />
                  <CloseIcon />
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "min-content",
                fontSize: 16,
                fontWeight: "bold",
                color: "#000",
                whiteSpace: "nowrap",
                "@media screen and (max-width:900px)": {
                  flexDirection: "column",
                  width: "100%",
                  alignItems: "flex-start",
                },
              }}
            >
              <Box sx={{ minWidth: "160px" }}>直近売上</Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  width: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    name="latestSalesTrue"
                    size="small"
                    checked={searchContents.latest_sales === true}
                    onChange={() => handleChangeSales(true)}
                  />
                  <PanoramaFishEyeIcon />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    name="latestSalesFalse"
                    size="small"
                    checked={searchContents.latest_sales === false}
                    onChange={() => handleChangeSales(false)}
                  />
                  <CloseIcon />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <SearchResult ref={searchResultRef} searchContent={searchContents} />
    </Box>
  );
});

SearchArea.displayName = 'SearchArea';

export default SearchArea;
